/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Report on one assignment in five fixed layers, ending with what it cannot show.
 * @state-owner dataset store owns the run; local state holds the student sort
 * @allowed-imports React; dataset and workspace stores; shared components and hooks; module model
 * @forbidden direct API client; student-only surfaces
 * @public-api run review header
 * @side-effects opens the assignment dialog, the answer view, the guide, and printing
 * @tests modules/reports/model/run-report.test.ts and the workspace Playwright flows
 */

import { useState } from 'react';
import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { findRun, runName } from '../../../shared/lib/selectors';
import { selectRunReport, useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { AssignmentDialog } from '../../activities';
import { runReport, runReportSummary, type StudentRow } from '../model/run-report';
import { ReportGuideDialog } from './ReportGuideDialog';

type SortKey = 'name' | 'correct' | 'concern';

export function ActivityInsightDialog({ runId }: { runId: string }) {
  const t = useTranslator();
  const { closeModal, openModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const answers = useDatasetStore((state) => state.answers);
  const seats = useDatasetStore((state) => state.seats);
  const report = useDatasetStore((state) => selectRunReport(state, runId));
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const [sort, setSort] = useState<SortKey>('name');
  const [openItemRow, setOpenItemRow] = useState<number | null>(null);

  const run = findRun(runs, runId);
  if (!run) return null;

  const data = runReport({ answers, classes, report, run, seats });
  const activityName = runName(activities, run);
  /** Two lines at most: who the data covers, and the one thing it cannot establish. */
  const primaryCaveats = data.caveats.filter((caveat) => caveat.primary).slice(0, 2);
  const restCount = data.caveats.length - primaryCaveats.length;
  const summary = runReportSummary(data, activityName);

  const sorted = [...data.students].sort((a: StudentRow, b: StudentRow) => {
    if (sort === 'correct') return a.correct - b.correct || a.name.localeCompare(b.name);
    if (sort === 'concern') {
      return (a.topConcern ?? '￿').localeCompare(b.topConcern ?? '￿');
    }
    return a.name.localeCompare(b.name);
  });

  /** Every jump lands on the answer sheet, which is where the actual work is. */
  const openAnswers = (student: string) => {
    closeModal();
    openItem({ k: 'ans', id: runId, stu: student });
  };

  const assign = (students: readonly string[]) => {
    openModal(
      <AssignmentDialog
        activityId={run.activityId}
        classIndex={run.classIndex}
        preselected={students}
      />,
    );
  };

  return (
    <ModalDocument
      className="repdoc"
      footer={
        <>
          <button
            className="bt pri"
            type="button"
            onClick={() => assign(data.topIssue ? (data.items[0]?.students ?? []) : [])}
          >
            {t('建立補強練習並指派')}
          </button>
          <button className="bt" type="button" onClick={() => window.print()}>
            {t('列印')}
          </button>
          <button className="bt" type="button" onClick={() => openModal(<ReportGuideDialog />)}>
            {t('報表判讀說明')}
          </button>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('關閉')}
          </button>
        </>
      }
      maxWidth={880}
      subtitle={`${classes[run.classIndex].name} · ${t(RUN_MODE_LABEL[run.mode])} · ${t(run.dateLabel)}`}
      title={activityName}
    >
      <section className="rpsum">
        {summary.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </section>

      <div className="rpgrid">
        <section className="rpblock">
          <header>
            <h6>{`${t('學生一覽')} · ${data.students.length}`}</h6>
            <div className="sp" />
            <label className="rpsort">
              {t('排序')}
              <select onChange={(event) => setSort(event.target.value as SortKey)} value={sort}>
                <option value="name">{t('姓名')}</option>
                <option value="correct">{t('答對最少')}</option>
                <option value="concern">{t('最需注意')}</option>
              </select>
            </label>
          </header>
          <div className="rpscroll">
            <table className="rptable">
              <thead>
                <tr>
                  <th>{t('姓名')}</th>
                  <th>{t('已答')}</th>
                  <th>{t('答對')}</th>
                  <th>{t('最需注意')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((student) => (
                  <tr key={student.name}>
                    <td>
                      <button
                        className="lk"
                        type="button"
                        onClick={() => openAnswers(student.name)}
                      >
                        {student.name}
                      </button>
                    </td>
                    <td>{`${student.answered}/${data.questions}`}</td>
                    <td>{student.correct}</td>
                    <td className="rpmut">{student.topConcern ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rpblock">
          <header>
            <h6>{`${t('逐題')} · ${data.items.length}`}</h6>
          </header>
          <div className="rpscroll">
            {data.items.map((item) => (
              <div className="rpitem" key={item.index}>
                <span className="rpq">{`Q${item.index + 1}`}</span>
                <span className="rpstem" title={item.stem}>
                  {item.stem}
                </span>
                <span className="rpcount">
                  {`${item.incorrect} ${t('人答錯')}`}
                  <em>{`(${t('共')} ${item.total} ${t('人')})`}</em>
                </span>
                <button
                  className="bt sm"
                  disabled={!item.incorrect}
                  type="button"
                  onClick={() => setOpenItemRow(openItemRow === item.index ? null : item.index)}
                >
                  {`${t('看這')} ${item.incorrect} ${t('人')}`}
                </button>
                {openItemRow === item.index ? (
                  <div className="rpwho">
                    {item.students.map((student) => (
                      <button
                        className="nlname"
                        key={student}
                        type="button"
                        onClick={() => openAnswers(student)}
                      >
                        <i>{student[0]}</i>
                        {student}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="rpblock">
        <header>
          <h6>{t('錯法')}</h6>
          <span className="rpmut">{t('跨兩題以上才顯示')}</span>
        </header>
        {data.misconceptions.length ? (
          data.misconceptions.map((row) => (
            <div className="rpitem" key={row.id}>
              <span className="rpstem">{t(row.label)}</span>
              <span className="rpcount">
                {`${row.items} ${t('題')} · ${row.students.length} ${t('人')}`}
              </span>
              <button className="bt sm" type="button" onClick={() => assign(row.students)}>
                {t('分組')}
              </button>
            </div>
          ))
        ) : (
          <p className="rpmut">{t('這次評量每個錯法只出現在一題，還不能聚合。')}</p>
        )}
      </section>

      <footer className="rpcav">
        {primaryCaveats.map((caveat) => (
          <p key={caveat.id}>{caveat.text}</p>
        ))}
        {restCount ? (
          <button
            className="lk rpmore"
            type="button"
            onClick={() => openModal(<ReportGuideDialog caveats={data.caveats} />)}
          >
            {`${t('還有')} ${restCount} ${t('項限制')}`}
          </button>
        ) : null}
      </footer>
    </ModalDocument>
  );
}
